import { httpClient } from '../utils/http-client'
import {
  type AiringChoice,
  type AllocationDto,
  type AnnualWindowsDto,
  BenchmarkIndex,
  type BenchmarkPointDto,
  type CalculationResult,
  type DiversificationCalculatorResponseDto,
  type DiversificationHoldingDto,
  type DiversificationHoldingSearchRequestDto,
  type EnumsResponse,
  type EtfDetailDto,
  type EtfHoldingBreakdownDto,
  type FundReportDto,
  type InstrumentDto,
  type InstrumentsResponse,
  type CutChoice,
  type CutContext,
  type CutReview,
  type JingleReview,
  type ManualCut,
  type ProgrammeChoice,
  type RadioDay,
  type RadioPlayerChoice,
  type RadioPlayerDto,
  type RadioPositionDto,
  type RadioProgrammeDto,
  type RadioSearch,
  type RadioSpot,
  type RadioStation,
  type Station,
  type RecordingSummary,
  type TranscriptSection,
  type IntradaySummaryPointDto,
  type PortfolioSummaryDto,
  type RangeChangeDto,
  TimeRange,
  type TransactionsWithSummaryDto,
  type XirrWindowsDto,
} from '../models/generated/domain-models'
import type { Page } from '../models/page'
import type { CachedState } from '../components/diversification/types'
import { API_ENDPOINTS } from '../constants'

const LOGO_TIMEOUT = 60000

const platformParams = (platforms?: string[]) => (platforms?.length ? { platforms } : {})

interface BuildInfo {
  hash: string
  time: string
}

export interface LogoCandidateDto {
  thumbnailUrl: string
  title: string
  index: number
  imageDataUrl?: string
}

export interface LogoReplacementRequest {
  holdingUuid: string
  candidateIndex: number
}

export interface LogoReplacementResponse {
  success: boolean
  message: string
}

export const enumService = {
  getAll: () => httpClient.get<EnumsResponse>(API_ENDPOINTS.ENUMS),
}

export const transactionsService = {
  getAll: (platforms?: string[], fromDate?: string, untilDate?: string) =>
    httpClient.get<TransactionsWithSummaryDto>(API_ENDPOINTS.TRANSACTIONS, {
      params: {
        ...platformParams(platforms),
        ...(fromDate ? { fromDate } : {}),
        ...(untilDate ? { untilDate } : {}),
      },
    }),

  getPlatforms: () => httpClient.get<string[]>(API_ENDPOINTS.TRANSACTIONS_PLATFORMS),
}

export const instrumentsService = {
  getAll: (platforms?: string[], period: TimeRange = TimeRange.ONE_DAY) =>
    httpClient.get<InstrumentsResponse>(API_ENDPOINTS.INSTRUMENTS, {
      params: { period, ...platformParams(platforms) },
    }),

  create: (data: Partial<InstrumentDto>) =>
    httpClient.post<InstrumentDto>(API_ENDPOINTS.INSTRUMENTS, data),

  update: (id: number | string, data: Partial<InstrumentDto>) =>
    httpClient.put<InstrumentDto>(`${API_ENDPOINTS.INSTRUMENTS}/${id}`, data),

  refreshPrices: () =>
    httpClient.post<{ status: string }>(API_ENDPOINTS.INSTRUMENTS_REFRESH_PRICES),
}

export const portfolioSummaryService = {
  getHistorical: (page: number, size: number, platforms?: string[]) =>
    httpClient.get<Page<PortfolioSummaryDto>>(API_ENDPOINTS.PORTFOLIO_SUMMARY_HISTORICAL, {
      params: { page, size, ...platformParams(platforms) },
    }),

  getCurrent: (platforms?: string[]) =>
    httpClient.get<PortfolioSummaryDto>(API_ENDPOINTS.PORTFOLIO_SUMMARY_CURRENT, {
      params: platformParams(platforms),
    }),

  getSeries: (range: TimeRange, platforms?: string[]) =>
    httpClient.get<PortfolioSummaryDto[]>(API_ENDPOINTS.PORTFOLIO_SUMMARY_SERIES, {
      params: { range, ...platformParams(platforms) },
    }),

  getIntraday: (range: TimeRange, platforms?: string[]) =>
    httpClient.get<IntradaySummaryPointDto[]>(API_ENDPOINTS.PORTFOLIO_SUMMARY_INTRADAY, {
      params: { range, ...platformParams(platforms) },
    }),

  getBenchmark: (range: TimeRange, index: BenchmarkIndex) =>
    httpClient.get<BenchmarkPointDto[]>(API_ENDPOINTS.PORTFOLIO_SUMMARY_BENCHMARK, {
      params: { range, index },
    }),

  getRangeChange: (range: TimeRange, platforms?: string[]) =>
    httpClient.get<RangeChangeDto>(API_ENDPOINTS.PORTFOLIO_SUMMARY_RANGE_CHANGE, {
      params: { range, ...platformParams(platforms) },
    }),

  getXirrWindows: (platforms?: string[]) =>
    httpClient.get<XirrWindowsDto>(API_ENDPOINTS.PORTFOLIO_SUMMARY_XIRR_WINDOWS, {
      params: platformParams(platforms),
    }),

  getAnnualWindows: (platforms?: string[]) =>
    httpClient.get<AnnualWindowsDto>(API_ENDPOINTS.PORTFOLIO_SUMMARY_ANNUAL_WINDOWS, {
      params: platformParams(platforms),
    }),

  recalculate: () =>
    httpClient.post<{ message: string }>(API_ENDPOINTS.PORTFOLIO_SUMMARY_RECALCULATE, undefined, {
      timeout: 60000,
    }),
}

export const diversificationService = {
  searchHoldings: (request: DiversificationHoldingSearchRequestDto) =>
    httpClient.post<Page<DiversificationHoldingDto>>(
      `${API_ENDPOINTS.DIVERSIFICATION}/holdings`,
      request
    ),

  getAvailableEtfs: () =>
    httpClient.get<EtfDetailDto[]>(`${API_ENDPOINTS.DIVERSIFICATION}/available-etfs`),

  calculate: (allocations: AllocationDto[]) =>
    httpClient.post<DiversificationCalculatorResponseDto>(
      `${API_ENDPOINTS.DIVERSIFICATION}/calculate`,
      { allocations }
    ),

  getConfig: () =>
    httpClient.get<CachedState>(`${API_ENDPOINTS.DIVERSIFICATION}/config`).catch(() => null),

  saveConfig: (config: CachedState) =>
    httpClient.put<CachedState>(`${API_ENDPOINTS.DIVERSIFICATION}/config`, config),
}

export const etfBreakdownService = {
  getBreakdown: (etfSymbols?: string[], platforms?: string[]) =>
    httpClient.get<EtfHoldingBreakdownDto[]>(API_ENDPOINTS.ETF_BREAKDOWN, {
      params: { ...(etfSymbols?.length ? { etfSymbols } : {}), ...platformParams(platforms) },
    }),

  getBenchmark: (symbol: string) =>
    httpClient.get<EtfHoldingBreakdownDto[]>(`${API_ENDPOINTS.ETF_BREAKDOWN}/benchmark`, {
      params: { symbol },
    }),
}

export const fundReportService = {
  getReports: (isin: string) =>
    httpClient.get<FundReportDto[]>(`${API_ENDPOINTS.FUNDS}/${isin}/reports`),

  getReportUrl: (isin: string, asOfDate: string): string =>
    `/api${API_ENDPOINTS.FUNDS}/${isin}/reports/${asOfDate}`,
}

export const logoService = {
  getCandidates: (holdingUuid: string) =>
    httpClient.get<LogoCandidateDto[]>(`${API_ENDPOINTS.LOGOS}/${holdingUuid}/candidates`, {
      timeout: LOGO_TIMEOUT,
    }),

  searchByName: (name: string) =>
    httpClient.get<LogoCandidateDto[]>(`${API_ENDPOINTS.LOGOS}/search`, {
      params: { name },
      timeout: LOGO_TIMEOUT,
    }),

  replaceLogo: (request: LogoReplacementRequest) =>
    httpClient.post<LogoReplacementResponse>(`${API_ENDPOINTS.LOGOS}/replace`, request, {
      timeout: LOGO_TIMEOUT,
    }),

  uploadLogo: (uuid: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return httpClient.post<LogoReplacementResponse>(`${API_ENDPOINTS.LOGOS}/${uuid}`, form, {
      timeout: LOGO_TIMEOUT,
    })
  },

  getLogoUrl: (uuid: string): string => `/api${API_ENDPOINTS.LOGOS}/${uuid}`,
}

export const utilityService = {
  getCalculationResult: () => httpClient.get<CalculationResult>(API_ENDPOINTS.CALCULATOR),

  getBuildInfo: () => httpClient.get<BuildInfo>(API_ENDPOINTS.BUILD_INFO),
}

export const monitoringService = {
  streamUrl: `/api${API_ENDPOINTS.MONITORING_COLLECTIONS}/stream`,
  rerun: (key: string) =>
    httpClient.post<void>(`${API_ENDPOINTS.MONITORING_COLLECTIONS}/${key}/rerun`),
}

export const radioService = {
  recordings: () => httpClient.get<RecordingSummary[]>(API_ENDPOINTS.RADIO),
  transcript: (name: string) =>
    httpClient.get<TranscriptSection[]>(`${API_ENDPOINTS.RADIO}/${name}/transcript`),
  cuts: (name: string) => httpClient.get<CutContext[]>(`${API_ENDPOINTS.RADIO}/${name}/cuts`),
  review: (name: string, review: CutReview) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/${name}/cuts`, review),
  addCut: (name: string, cut: ManualCut) =>
    httpClient.post<void>(`${API_ENDPOINTS.RADIO}/${name}/cuts/manual`, cut),
  findCuts: (name: string, choice: CutChoice) =>
    httpClient.post<void>(`${API_ENDPOINTS.RADIO}/${name}/cuts/find`, choice),
  dropCut: (name: string, cut: number) =>
    httpClient.delete<void>(`${API_ENDPOINTS.RADIO}/${name}/cuts/${cut}`),
  deleteRecording: (name: string) => httpClient.delete<void>(`${API_ENDPOINTS.RADIO}/${name}`),
  jingle: (name: string, cut: number, review: JingleReview) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/${name}/cuts/${cut}/jingle`, review),
  position: (name: string) =>
    httpClient.get<RadioPositionDto>(`${API_ENDPOINTS.RADIO}/${name}/position`),
  savePosition: (name: string, position: RadioPositionDto) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/${name}/position`, position),
  player: () => httpClient.get<RadioPlayerDto>(`${API_ENDPOINTS.RADIO}/player`),
  savePlayer: (choice: RadioPlayerChoice) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/player`, choice),
  saveSpot: (spot: RadioSpot) => httpClient.put<void>(`${API_ENDPOINTS.RADIO}/player/spot`, spot),
  fileUrl: (name: string, file: string) => `/api${API_ENDPOINTS.RADIO}/${name}/${file}`,
  programmes: () => httpClient.get<RadioProgrammeDto[]>(`${API_ENDPOINTS.RADIO}/programmes`),
  stations: () => httpClient.get<RadioStation[]>(`${API_ENDPOINTS.RADIO}/station`),
  liveUrl: `/api${API_ENDPOINTS.RADIO}/live`,
  choose: (id: number, choice: ProgrammeChoice) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/programmes/${id}`, choice),
  day: (date: string | null, station: Station | null) =>
    httpClient.get<RadioDay>(`${API_ENDPOINTS.RADIO}/schedule`, { params: { date, station } }),
  record: (id: number, choice: AiringChoice) =>
    httpClient.put<void>(`${API_ENDPOINTS.RADIO}/airings/${id}`, choice),
  search: (q: string, after: string | null, station: Station | null) =>
    httpClient.get<RadioSearch>(`${API_ENDPOINTS.RADIO}/search`, {
      params: { q, after, station },
    }),
}

export const liveUpdatesService = {
  streamUrl: `/api${API_ENDPOINTS.LIVE_UPDATES}`,
}
